import { useRef, useState, useEffect, createRef, useContext, useCallback, LegacyRef } from "react";
import styles from "./cvssCalculator.module.css";
import SegmentedControl, { type Segment } from "../../../../../Common/Forms/SegmentedControl/SegmentedControl";
import {
  type CVSSVector,
  cvssVectorToString,
  defaultVector,
  isValidCVSS31Vector,
  parseCVSSVector,
} from "./CvssFunctions";
import { getRating, getScore } from "cvss";
import { EditorContext } from "../../../../../../contexts/EditorContext";
import SlateEditor, { ListingRef } from "../EditorWidget/components/SlateEditor/SlateEditor";

interface Props {
  editorRef: LegacyRef<ListingRef>;
}
export default function CVSSCalculator({editorRef}: Readonly<Props>) {
  const { value, setValue } = useContext(EditorContext);

  const [useCustomScore, setUseCustomScore] = useState(!isValidCVSS31Vector(value));
  const [vector, setVector] = useState<CVSSVector>(parseCVSSVector(value) ?? defaultVector);
  const [score, setScore] = useState(0);
  const [severity, setSeverity] = useState("None");

  // Refs for segmented controls
  const avControlRef = useRef<HTMLDivElement>(null);
  const acControlRef = useRef<HTMLDivElement>(null);
  const prControlRef = useRef<HTMLDivElement>(null);
  const uiControlRef = useRef<HTMLDivElement>(null);
  const sControlRef = useRef<HTMLDivElement>(null);
  const cControlRef = useRef<HTMLDivElement>(null);
  const iControlRef = useRef<HTMLDivElement>(null);
  const aControlRef = useRef<HTMLDivElement>(null);

  // Segment definitions
  const avSegments: Segment[] = [
    { label: "Network (N)", value: "N", ref: createRef() },
    { label: "Adjacent (A)", value: "A", ref: createRef() },
    { label: "Local (L)", value: "L", ref: createRef() },
    { label: "Physical (P)", value: "P", ref: createRef() },
  ];

  const acSegments: Segment[] = [
    { label: "Low (L)", value: "L", ref: createRef() },
    { label: "High (H)", value: "H", ref: createRef() },
  ];

  const prSegments: Segment[] = [
    { label: "None (N)", value: "N", ref: createRef() },
    { label: "Low (L)", value: "L", ref: createRef() },
    { label: "High (H)", value: "H", ref: createRef() },
  ];

  const uiSegments: Segment[] = [
    { label: "None (N)", value: "N", ref: createRef() },
    { label: "Required (R)", value: "R", ref: createRef() },
  ];

  const sSegments: Segment[] = [
    { label: "Unchanged (U)", value: "U", ref: createRef() },
    { label: "Changed (C)", value: "C", ref: createRef() },
  ];

  const ciaSegments: Segment[] = [
    { label: "None (N)", value: "N", ref: createRef() },
    { label: "Low (L)", value: "L", ref: createRef() },
    { label: "High (H)", value: "H", ref: createRef() },
  ];

  const iSegments: Segment[] = [
    { label: "None (N)", value: "N", ref: createRef() },
    { label: "Low (L)", value: "L", ref: createRef() },
    { label: "High (H)", value: "H", ref: createRef() },
  ];

  const saveVectorData = useCallback(() => {
    setValue(cvssVectorToString(vector));
  }, [setValue, vector]);

  // Calculate CVSS score when vector changes
  useEffect(() => {
    const calculatedScore = getScore(vector as unknown as string);
    setScore(calculatedScore);
    setSeverity(getRating(calculatedScore));
    console.log("Score:", calculatedScore, "Rating: ", getRating(calculatedScore));

    saveVectorData();
  }, [vector, saveVectorData]);

  const handleVectorChange = (metric: string, value: string) => {
    setVector((prev) => ({ ...prev, [metric]: value }));
  };

  const toggleCustomScore = () => {
    setUseCustomScore(!useCustomScore);
  };

  // Find default index for each segment
  const getDefaultIndex = (segments: Segment[], currentValue: string) => {
    const index = segments.findIndex((segment) => segment.value === currentValue) || 0;
    return index === -1 ? 0 : index;
  };

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <h2 className={styles.cardTitle}>{!useCustomScore && "CVSS Score Calculator"}</h2>
        <div className={styles.toggleContainer}>
          <label className={styles.toggleLabel}>
            <input
              type="checkbox"
              checked={useCustomScore}
              onChange={toggleCustomScore}
              className={styles.toggleInput}
            />
            <span className={styles.toggleSlider}></span>
            <span className={styles.toggleText}>Use custom scoring</span>
          </label>
        </div>
      </div>

      {!useCustomScore ? (
          <div className={styles.cardContent}>
            <div className={styles.grid}>
              <div className={styles.formGroup}>
                <div className={styles.label}>Attack Vector (AV)</div>
                <SegmentedControl
                  name="AV"
                  segments={avSegments}
                  callback={(value) => handleVectorChange("AV", value)}
                  defaultIndex={getDefaultIndex(avSegments, vector.AV)}
                  controlRef={avControlRef}
                />
              </div>

              <div className={styles.formGroup}>
                <div className={styles.label}>Attack Complexity (AC)</div>
                <SegmentedControl
                  name="AC"
                  segments={acSegments}
                  callback={(value) => handleVectorChange("AC", value)}
                  defaultIndex={getDefaultIndex(acSegments, vector.AC)}
                  controlRef={acControlRef}
                />
              </div>

              <div className={styles.formGroup}>
                <div className={styles.label}>Privileges Required (PR)</div>
                <SegmentedControl
                  name="PR"
                  segments={prSegments}
                  callback={(value) => handleVectorChange("PR", value)}
                  defaultIndex={getDefaultIndex(prSegments, vector.PR)}
                  controlRef={prControlRef}
                />
              </div>

              <div className={styles.formGroup}>
                <div className={styles.label}>User Interaction (UI)</div>
                <SegmentedControl
                  name="UI"
                  segments={uiSegments}
                  callback={(value) => handleVectorChange("UI", value)}
                  defaultIndex={getDefaultIndex(uiSegments, vector.UI)}
                  controlRef={uiControlRef}
                />
              </div>

              <div className={styles.formGroup}>
                <div className={styles.label}>Scope (S)</div>
                <SegmentedControl
                  name="S"
                  segments={sSegments}
                  callback={(value) => handleVectorChange("S", value)}
                  defaultIndex={getDefaultIndex(sSegments, vector.S)}
                  controlRef={sControlRef}
                />
              </div>

              <div className={styles.formGroup}>
                <div className={styles.label}>Confidentiality (C)</div>
                <SegmentedControl
                  name="C"
                  segments={ciaSegments}
                  callback={(value) => handleVectorChange("C", value)}
                  defaultIndex={getDefaultIndex(ciaSegments, vector.C)}
                  controlRef={cControlRef}
                />
              </div>

              <div className={styles.formGroup}>
                <div className={styles.label}>Integrity (I)</div>
                <SegmentedControl
                  name="I"
                  segments={iSegments}
                  callback={(value) => handleVectorChange("I", value)}
                  defaultIndex={getDefaultIndex(ciaSegments, vector.I)}
                  controlRef={iControlRef}
                />
              </div>

              <div className={styles.formGroup}>
                <div className={styles.label}>Availability (A)</div>
                <SegmentedControl
                  name="A"
                  segments={ciaSegments}
                  callback={(value) => handleVectorChange("A", value)}
                  defaultIndex={getDefaultIndex(ciaSegments, vector.A)}
                  controlRef={aControlRef}
                />
              </div>
            </div>

          <div className={styles.cardFooter}>
            <div className={styles.scoreSection}>
              <p className={styles.resultLabel}>CVSS Score:</p>
              <div className={styles.scoreDisplay}>
                <span className={`${styles.scoreBadge} ${styles[`severity${severity}`]}`}>{score.toFixed(1)}</span>
                <span className={styles.severityText}>{severity}</span>
              </div>
            </div>

            <div className={styles.vectorContainer}>
              <p className={styles.resultLabel}>Vector String:</p>
              <p className={styles.vectorString}>{cvssVectorToString(vector)}</p>
            </div>
          </div>
          </div>
      ) : (
        <SlateEditor ref={editorRef} />
      )}
    </div>
  );
}
